// src/lib/booking-submission.ts
//
// Single funnel for creating an appointment from the booking wizard.
// Owns two safety behaviours:
//  - a SYNCHRONOUS in-flight/one-shot guard, so two rapid clicks cannot start
//    two POSTs before React state updates, and nothing can resubmit after a
//    confirmed booking until reset() (full wizard reset / fresh open);
//  - the generate_lead conversion, fired only after persistence is confirmed
//    and WITHOUT the API response — the returned booking ID never reaches
//    analytics. trackAnalyticsEvent itself blocks private routes, so bookings
//    completed inside /patient/* emit nothing.

import { trackAnalyticsEvent } from '@/lib/analytics'

type BookingSubmitterDeps<TPayload> = {
  post: (payload: TPayload) => Promise<unknown>
  onSuccess: () => void
  onError: (error: unknown) => void
}

export type BookingSubmitter<TPayload> = {
  /** Returns true only when this call performed a successful booking. */
  submit: (payload: TPayload) => Promise<boolean>
  /** Unlocks the one-shot guard for a genuinely new booking. */
  reset: () => void
}

export function createBookingSubmitter<TPayload>(
  deps: BookingSubmitterDeps<TPayload>,
): BookingSubmitter<TPayload> {
  let inFlight = false
  let completed = false

  return {
    async submit(payload: TPayload): Promise<boolean> {
      if (inFlight || completed) return false
      inFlight = true
      try {
        await deps.post(payload)
        completed = true
        trackAnalyticsEvent('generate_lead', {
          lead_type: 'appointment_booking',
        })
        deps.onSuccess()
        return true
      } catch (error) {
        // Failed request: no conversion, and the guard unlocks (via finally)
        // so the user can retry.
        deps.onError(error)
        return false
      } finally {
        inFlight = false
      }
    },
    reset() {
      // Only the one-shot lock is cleared; an in-flight request keeps its own
      // lock until it settles, so reset() can never enable a concurrent POST.
      completed = false
    },
  }
}
